import { useMemo, useState } from "react";
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths, startOfYear } from "date-fns";
import { Filter, Plus, X, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export type FieldKind = "text" | "enum" | "number" | "date";

export interface FilterField {
  key: string; // lead column, or "cf:<name>" for custom fields
  label: string;
  kind: FieldKind;
  options?: { value: string; label: string }[];
}

export interface FilterRule {
  id: string;
  field: string;
  op: string;
  value?: string;
  value2?: string;
  values?: string[];
  preset?: string;
  from?: string; // ISO
  to?: string; // ISO
}

export const STATUS_OPTIONS = [
  ["new", "New"], ["contacted", "Contacted"], ["qualified", "Qualified"], ["proposal", "Proposal"],
  ["negotiation", "Negotiation"], ["won", "Won"], ["lost", "Lost"], ["do_not_contact", "Do Not Contact"],
].map(([value, label]) => ({ value, label }));
export const PRIORITY_OPTIONS = [["low", "Low"], ["medium", "Medium"], ["high", "High"]].map(([value, label]) => ({ value, label }));

const OPS: Record<FieldKind, { value: string; label: string }[]> = {
  text: [
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "is", label: "is" },
    { value: "empty", label: "is empty" },
    { value: "not_empty", label: "is not empty" },
  ],
  enum: [{ value: "any", label: "is any of" }],
  number: [
    { value: "eq", label: "equals" },
    { value: "gt", label: "greater than" },
    { value: "lt", label: "less than" },
    { value: "between", label: "between" },
  ],
  date: [{ value: "range", label: "is within" }],
};

export const DATE_PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["last7", "Last 7 days"], ["last30", "Last 30 days"],
  ["this_month", "This month"], ["last_month", "Last month"], ["this_year", "This year"], ["custom", "Custom range"],
] as const;

export const resolveDateRange = (r: FilterRule): [Date | null, Date | null] => {
  const now = new Date();
  switch (r.preset) {
    case "today": return [startOfDay(now), endOfDay(now)];
    case "yesterday": { const y = subDays(now, 1); return [startOfDay(y), endOfDay(y)]; }
    case "last7": return [startOfDay(subDays(now, 6)), endOfDay(now)];
    case "last30": return [startOfDay(subDays(now, 29)), endOfDay(now)];
    case "this_month": return [startOfMonth(now), endOfMonth(now)];
    case "last_month": { const m = subMonths(now, 1); return [startOfMonth(m), endOfMonth(m)]; }
    case "this_year": return [startOfYear(now), endOfDay(now)];
    case "custom":
      return [r.from ? startOfDay(new Date(r.from)) : null, r.to ? endOfDay(new Date(r.to)) : null];
    default: return [null, null];
  }
};

/** True when the rule has enough input to filter. */
export const isRuleActive = (r: FilterRule, kind?: FieldKind) => {
  if (!kind) return false;
  if (kind === "enum") return (r.values?.length ?? 0) > 0;
  if (kind === "date") return !!r.preset && (r.preset !== "custom" || !!r.from || !!r.to);
  if (kind === "text") return r.op === "empty" || r.op === "not_empty" || !!r.value?.trim();
  if (kind === "number") return r.value !== undefined && r.value !== "" && (r.op !== "between" || (r.value2 ?? "") !== "");
  return false;
};

export const ruleMatches = (raw: unknown, r: FilterRule, kind: FieldKind): boolean => {
  const str = raw == null ? "" : String(raw);
  if (kind === "text") {
    const s = str.toLowerCase(), v = (r.value ?? "").trim().toLowerCase();
    switch (r.op) {
      case "contains": return s.includes(v);
      case "not_contains": return !s.includes(v);
      case "is": return s.trim() === v;
      case "empty": return s.trim() === "";
      case "not_empty": return s.trim() !== "";
    }
    return true;
  }
  if (kind === "enum") return (r.values ?? []).includes(str);
  if (kind === "number") {
    const cleaned = str.replace(/[,$\s]/g, "");
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return false;
    const n = parseFloat(cleaned), a = parseFloat(r.value ?? ""), b = parseFloat(r.value2 ?? "");
    switch (r.op) {
      case "eq": return n === a;
      case "gt": return n > a;
      case "lt": return n < a;
      case "between": return n >= Math.min(a, b) && n <= Math.max(a, b);
    }
    return true;
  }
  // date
  if (!str) return false;
  const t = Date.parse(str);
  if (isNaN(t)) return false;
  const [from, to] = resolveDateRange(r);
  if (from && t < from.getTime()) return false;
  if (to && t > to.getTime()) return false;
  return true;
};

const newRule = (field: FilterField): FilterRule => ({
  id: Math.random().toString(36).slice(2),
  field: field.key,
  op: OPS[field.kind][0].value,
  preset: field.kind === "date" ? "last7" : undefined,
  values: field.kind === "enum" ? [] : undefined,
});

const DateButton = ({ value, placeholder, onChange }: { value?: string; placeholder: string; onChange: (v?: string) => void }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className={cn("flex-1 justify-start font-normal", !value && "text-muted-foreground")}>
        <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
        {value ? format(new Date(value), "MMM d, yyyy") : placeholder}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar
        mode="single"
        selected={value ? new Date(value) : undefined}
        onSelect={(d) => onChange(d ? d.toISOString() : undefined)}
        initialFocus
        className={cn("p-3 pointer-events-auto")}
      />
    </PopoverContent>
  </Popover>
);

interface Props {
  fields: FilterField[];
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
}

const LeadFilterPanel = ({ fields, rules, onChange }: Props) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const byKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);
  const activeCount = rules.filter((r) => isRuleActive(r, byKey[r.field]?.kind)).length;

  const update = (id: string, patch: Partial<FilterRule>) =>
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const body = (
    <div className="space-y-3">
      {rules.length === 0 && (
        <p className="text-sm text-muted-foreground">No filters yet. Add one to narrow down this sheet.</p>
      )}
      {rules.map((r) => {
        const f = byKey[r.field];
        if (!f) return null;
        return (
          <div key={r.id} className="rounded-md border border-border p-2.5 space-y-2 bg-muted/20">
            <div className="flex gap-2">
              <Select value={r.field} onValueChange={(k) => { const nf = byKey[k]; onChange(rules.map((x) => (x.id === r.id ? { ...newRule(nf), id: r.id } : x))); }}>
                <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {fields.map((ff) => <SelectItem key={ff.key} value={ff.key} className="text-xs">{ff.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {OPS[f.kind].length > 1 && (
                <Select value={r.op} onValueChange={(op) => update(r.id, { op })}>
                  <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPS[f.kind].map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => onChange(rules.filter((x) => x.id !== r.id))} aria-label="Remove filter">
                <X className="h-4 w-4" />
              </Button>
            </div>

            {f.kind === "text" && r.op !== "empty" && r.op !== "not_empty" && (
              <Input className="h-8 text-xs" placeholder="Value" value={r.value ?? ""} onChange={(e) => update(r.id, { value: e.target.value })} />
            )}

            {f.kind === "number" && (
              <div className="flex gap-2">
                <Input type="number" className="h-8 text-xs" placeholder={r.op === "between" ? "Min" : "Value"} value={r.value ?? ""} onChange={(e) => update(r.id, { value: e.target.value })} />
                {r.op === "between" && (
                  <Input type="number" className="h-8 text-xs" placeholder="Max" value={r.value2 ?? ""} onChange={(e) => update(r.id, { value2: e.target.value })} />
                )}
              </div>
            )}

            {f.kind === "enum" && (
              <div className="grid grid-cols-2 gap-1.5">
                {f.options?.map((o) => {
                  const checked = r.values?.includes(o.value) ?? false;
                  return (
                    <label key={o.value} className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) =>
                          update(r.id, { values: c ? [...(r.values ?? []), o.value] : (r.values ?? []).filter((v) => v !== o.value) })
                        }
                      />
                      {o.label}
                    </label>
                  );
                })}
              </div>
            )}

            {f.kind === "date" && (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {DATE_PRESETS.map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => update(r.id, { preset: v })}
                      className={cn(
                        "px-2 py-1 rounded-md border text-[11px] transition-colors",
                        r.preset === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted",
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                {r.preset === "custom" && (
                  <div className="flex gap-2">
                    <DateButton value={r.from} placeholder="From" onChange={(v) => update(r.id, { from: v })} />
                    <DateButton value={r.to} placeholder="To" onChange={(v) => update(r.id, { to: v })} />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      <div className="flex items-center justify-between pt-1">
        <Select value="" onValueChange={(k) => onChange([...rules, newRule(byKey[k])])}>
          <SelectTrigger className="h-8 w-auto gap-1.5 text-xs">
            <Plus className="h-3.5 w-3.5" /><span>Add filter</span>
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {fields.map((f) => <SelectItem key={f.key} value={f.key} className="text-xs">{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {rules.length > 0 && (
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => onChange([])}>
            Clear all
          </button>
        )}
      </div>
    </div>
  );

  const trigger = (
    <Button variant="outline" size="sm" className="shrink-0 gap-2">
      <Filter className="h-4 w-4" />
      <span className="hidden sm:inline">Filter</span>
      {activeCount > 0 && (
        <span className="ml-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-mono-accent px-1.5 leading-4">{activeCount}</span>
      )}
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader className="mb-3"><SheetTitle>Filter leads</SheetTitle></SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] max-h-[70vh] overflow-y-auto">
        <p className="text-sm font-semibold mb-3">Filter leads</p>
        {body}
      </PopoverContent>
    </Popover>
  );
};

export default LeadFilterPanel;
